import { Controller } from "@hotwired/stimulus";

// Gives a button/link an immediate, non-repeatable "loading" state: disables it
// and swaps the label for a spinner (.is-loading, styled in online_ordering.css)
// so the user gets feedback and can't double-click / double-submit.
//
//   click->button-loader#disableLink       on an <a>/<button> that navigates
//   submit->button-loader#disableOnSubmit   on a <form> — mark the submit
//                                            button with
//                                            data-button-loader-target="button"
export default class extends Controller {
  static targets = ["button"];

  connect() {
    // Restore any button locked before navigation (Turbolinks cache restore)
    // and again on bfcache restores, where connect() never re-runs.
    this.reset();
    this.onPageShow = (event) => {
      if (event.persisted) this.reset();
    };
    window.addEventListener("pageshow", this.onPageShow);
  }

  disconnect() {
    window.removeEventListener("pageshow", this.onPageShow);
  }

  reset() {
    this.element
      .querySelectorAll("[data-loading='true']")
      .forEach((el) => this.release(el));
    if (this.element.dataset.loading === "true") this.release(this.element);
  }

  release(el) {
    delete el.dataset.loading;
    el.classList.remove("is-loading", "disabled");
    el.removeAttribute("aria-disabled");
  }

  // For links/buttons that navigate on their own (e.g. the Back button).
  disableLink(event) {
    const el = event.currentTarget;
    if (el.dataset.loading === "true") {
      event.preventDefault();
      return;
    }
    this.engage(el);
  }

  // For real form submits — fires only after the browser's native validation
  // passes, so the button never sticks in a loading state on an invalid form.
  // Disabling the button here does not cancel the in-flight submission.
  disableOnSubmit() {
    const btn = this.hasButtonTarget
      ? this.buttonTarget
      : this.element.querySelector("[type=submit]");
    if (btn) this.engage(btn);
  }

  // No Bootstrap `disabled` class: on a plain `.btn` (no `.btn-*` variant) it
  // resolves background-color from an undefined --bs-btn-disabled-bg, which drops
  // the declaration and falls the button back to transparent. `.is-loading`
  // already sets pointer-events: none, and aria-disabled covers assistive tech.
  engage(el) {
    el.dataset.loading = "true";
    el.classList.add("is-loading");
    el.setAttribute("aria-disabled", "true");
  }
}
